import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";

export const meta = {
  slug: "how-to-calculate-what-you-owe-1099",
  title: "How to Calculate What You Owe as a 1099 Contractor",
  description: "Step-by-step guide to estimating your tax liability as an independent contractor, including federal, state, and self-employment taxes.",
  date: "2025-01-13",
  tags: ["tax calculation", "1099", "estimated taxes", "self-employment tax"]
};

export default function HowToCalculateWhatYouOwe1099() {
  return (
    <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
      <Helmet>
        <title>How to Calculate What You Owe as a 1099 Contractor (2025 Guide) | Free1099Calc</title>
        <meta
          name="description"
          content="Step-by-step guide to calculate your 1099 taxes. Learn federal income tax, self-employment tax, quarterly payments, and deductions for freelancers and independent contractors."
        />
        <meta name="keywords" content="1099 tax calculation, how to calculate 1099 taxes, self-employment tax calculator, freelance tax estimate, independent contractor taxes, quarterly tax payment calculator, 1099 income tax rate" />
        <link rel="canonical" href="https://free1099calc.com/blog/how-to-calculate-what-you-owe-1099" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://free1099calc.com/blog/how-to-calculate-what-you-owe-1099" />
        <meta property="og:title" content="How to Calculate 1099 Taxes: Complete Guide (2025)" />
        <meta property="og:description" content="Step-by-step guide to calculate federal, state, and self-employment taxes for 1099 contractors and freelancers." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=630&fit=crop&q=80" />
        <meta property="article:published_time" content="2025-01-13T00:00:00Z" />
        <meta property="article:author" content="Free1099Calc Team" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to Calculate 1099 Taxes (2025)" />
        <meta name="twitter:description" content="Complete tax calculation guide for freelancers and independent contractors." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=630&fit=crop&q=80" />
        
        <meta name="author" content="Free1099Calc Team" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "How to Calculate What You Owe as a 1099 Contractor",
            "description": "Step-by-step guide to calculating federal, state, and self-employment taxes for independent contractors.",
            "image": "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=630&fit=crop&q=80",
            "author": {
              "@type": "Organization",
              "name": "Free1099Calc"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Free1099Calc",
              "logo": {
                "@type": "ImageObject",
                "url": "https://free1099calc.com/logo.png"
              }
            },
            "datePublished": "2025-01-13",
            "dateModified": "2025-01-13",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://free1099calc.com/blog/how-to-calculate-what-you-owe-1099"
            }
          })}
        </script>
        
        {/* HowTo Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to Calculate 1099 Taxes",
            "description": "Calculate your tax liability as a 1099 independent contractor",
            "totalTime": "PT15M",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Calculate Total 1099 Income",
                "text": "Add up all 1099-NEC and 1099-MISC income from all sources for the year",
                "position": 1
              },
              {
                "@type": "HowToStep",
                "name": "Subtract Business Deductions",
                "text": "Deduct business expenses like supplies, mileage, home office, insurance, and professional services",
                "position": 2
              },
              {
                "@type": "HowToStep",
                "name": "Calculate Self-Employment Tax",
                "text": "Multiply net profit by 92.35% then by 15.3% to calculate SE tax",
                "position": 3
              },
              {
                "@type": "HowToStep",
                "name": "Determine Federal Income Tax",
                "text": "Apply your tax bracket to taxable income after standard/itemized deduction",
                "position": 4
              },
              {
                "@type": "HowToStep",
                "name": "Add State and Local Taxes",
                "text": "Calculate state income tax based on your state's tax rate",
                "position": 5
              }
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What percentage should I set aside for 1099 taxes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most 1099 contractors should set aside 25-30% of their gross income for taxes. This covers federal income tax (10-24%), self-employment tax (15.3%), and state income tax if applicable."
                }
              },
              {
                "@type": "Question",
                "name": "What is the self-employment tax rate?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The self-employment tax rate is 15.3% (12.4% for Social Security and 2.9% for Medicare). This applies to 92.35% of your net self-employment income. You can deduct half of your SE tax (7.65%) on your tax return."
                }
              },
              {
                "@type": "Question",
                "name": "How do I calculate quarterly estimated taxes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Calculate your expected annual tax liability, then divide by 4 for quarterly payments. Pay quarterly taxes by April 15, June 15, September 15, and January 15 to avoid IRS penalties."
                }
              }
            ]
          })}
        </script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://free1099calc.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://free1099calc.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "How to Calculate 1099 Taxes",
                "item": "https://free1099calc.com/blog/how-to-calculate-what-you-owe-1099"
              }
            ]
          })}
        </script>
      </Helmet>

      <article className="prose prose-slate max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold mb-3">
            How to Calculate What You Owe as a 1099 Contractor
          </h1>
          <p className="text-lg text-slate-600">
            Published January 13, 2025 • 8 min read
          </p>
        </header>

        <div className="bg-blue-50 border-l-4 border-primary-600 p-5 rounded-lg mb-8">
          <p className="text-slate-800 font-semibold mb-2">Quick Answer:</p>
          <p className="text-slate-700 mb-0">
            Most 1099 contractors owe <strong>25-30% of gross income</strong> in combined federal, state, and self-employment taxes. 
            Use the formula: <code className="bg-white px-2 py-1 rounded">(Gross Income - Deductions) × Tax Rate</code> to estimate your liability.
          </p>
        </div>

        {/* Calculator CTA */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <svg className="w-12 h-12 flex-shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">Calculate Your Exact Tax in 60 Seconds</h3>
              <p className="text-primary-100 mb-4 text-sm">
                Skip the manual math. Our free calculator estimates federal, state, and self-employment taxes instantly.
              </p>
              <Link 
                to="/" 
                className="inline-block bg-white text-primary-700 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-md"
              >
                Use Free Calculator →
              </Link>
            </div>
          </div>
        </div>

        <section className="mb-8">
          <p className="text-slate-700 mb-4">
            When you're self-employed or work as a 1099 independent contractor, <strong>taxes aren't withheld from your payments</strong>. 
            You're responsible for calculating and paying federal income tax, self-employment tax (Social Security + Medicare), 
            and state income tax on your own.
          </p>
          <p className="text-slate-700 mb-4">
            Unlike W-2 employees who have taxes automatically deducted, you must estimate your tax liability and pay 
            quarterly estimated taxes to avoid penalties. Here's exactly how to calculate what you owe.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 1: Calculate Total 1099 Income</h2>
          <p className="text-slate-700 mb-4">
            Add up all payments received from clients and platforms during the tax year:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
            <li><strong>1099-NEC forms:</strong> Non-employee compensation (freelance/contract work)</li>
            <li><strong>1099-MISC forms:</strong> Miscellaneous income (rents, royalties)</li>
            <li><strong>1099-K forms:</strong> Payment card and third-party network transactions (Stripe, PayPal, Venmo)</li>
            <li><strong>Cash/check payments:</strong> Even if you didn't receive a 1099, all income must be reported</li>
          </ul>
          
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">Example:</h3>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Client A (1099-NEC): $45,000</li>
              <li>• Client B (1099-NEC): $28,000</li>
              <li>• Etsy sales (1099-K): $12,000</li>
              <li>• Cash jobs: $3,500</li>
              <li className="pt-2 border-t mt-2 font-semibold">= Total Gross Income: $88,500</li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 2: Subtract Business Deductions</h2>
          <p className="text-slate-700 mb-4">
            Deduct ordinary and necessary business expenses to arrive at <strong>net profit</strong> (reported on Schedule C):
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Common Deductions:</h3>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                <li>Home office deduction</li>
                <li>Vehicle mileage (67¢/mile 2025)</li>
                <li>Office supplies and equipment</li>
                <li>Software subscriptions</li>
                <li>Internet and phone (business %)</li>
                <li>Professional services (CPA, lawyer)</li>
                <li>Health insurance premiums</li>
                <li>Advertising and marketing</li>
              </ul>
            </div>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Industry-Specific:</h3>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                <li><strong>Designers:</strong> Adobe CC, Canva, fonts</li>
                <li><strong>Developers:</strong> Hosting, domains, APIs</li>
                <li><strong>Consultants:</strong> Travel, meals (50%)</li>
                <li><strong>Contractors:</strong> Tools, materials, permits</li>
                <li><strong>Rideshare:</strong> Gas, maintenance, tolls</li>
                <li><strong>Delivery:</strong> Insulated bags, phone mount</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-5 rounded mb-4">
            <p className="text-sm text-slate-800 mb-2"><strong>Pro Tip:</strong></p>
            <p className="text-sm text-slate-700">
              Track every business expense meticulously. Missing deductions = overpaying taxes. Use our <Link to="/expense-tracker" className="text-green-700 underline font-semibold">free expense tracker</Link> to categorize and organize receipts automatically.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">Example (continued):</h3>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Gross Income: $88,500</li>
              <li>• Home office deduction: -$1,500</li>
              <li>• Vehicle mileage (15,000 mi): -$10,050</li>
              <li>• Equipment & supplies: -$3,200</li>
              <li>• Software/internet: -$2,400</li>
              <li>• Health insurance: -$7,200</li>
              <li>• Professional fees: -$1,500</li>
              <li className="pt-2 border-t mt-2 font-semibold">= Net Profit (Schedule C): $62,650</li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 3: Calculate Self-Employment Tax</h2>
          <p className="text-slate-700 mb-4">
            Self-employment (SE) tax covers Social Security and Medicare. It's calculated on <strong>92.35% of net profit</strong>:
          </p>
          
          <div className="bg-primary-50 border-l-4 border-primary-600 p-5 rounded mb-6">
            <p className="font-semibold text-slate-800 mb-2">SE Tax Formula:</p>
            <code className="bg-white px-3 py-2 rounded block text-sm text-slate-800">
              Net Profit × 92.35% × 15.3% = Self-Employment Tax
            </code>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              <li>• 12.4% for Social Security (up to $168,600 in 2025)</li>
              <li>• 2.9% for Medicare (no income limit)</li>
              <li>• 0.9% Additional Medicare Tax (income over $200k single / $250k married)</li>
            </ul>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-4">
            <h3 className="font-semibold text-lg mb-3">Example (continued):</h3>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Net Profit: $62,650</li>
              <li>• Net SE Income: $62,650 × 92.35% = $57,857</li>
              <li>• SE Tax: $57,857 × 15.3% = <strong>$8,852</strong></li>
              <li className="pt-2 mt-2">
                <strong>Important:</strong> You can deduct half of SE tax ($4,426) as an "above-the-line" deduction
              </li>
            </ul>
          </div>

          <p className="text-slate-700 text-sm italic">
            Note: Social Security tax caps at $168,600 (2025). Income above this is only subject to 2.9% Medicare tax.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 4: Calculate Federal Income Tax</h2>
          <p className="text-slate-700 mb-4">
            Federal income tax is based on your <strong>taxable income</strong> after deductions:
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-3">Taxable Income Calculation:</h3>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Net Profit: $62,650</li>
              <li>• Minus: SE tax deduction: -$4,426</li>
              <li>• Minus: QBI deduction (20%): -$11,645</li>
              <li>• Minus: Standard deduction (single 2025): -$14,600</li>
              <li className="pt-2 border-t mt-2 font-semibold">= Taxable Income: $31,979</li>
            </ul>
          </div>

          <div className="overflow-x-auto mb-6">
            <h3 className="font-semibold mb-3">2025 Tax Brackets (Single Filer):</h3>
            <table className="min-w-full border border-slate-200 text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-200 px-4 py-2 text-left">Taxable Income</th>
                  <th className="border border-slate-200 px-4 py-2 text-left">Tax Rate</th>
                  <th className="border border-slate-200 px-4 py-2 text-left">Tax Owed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-200 px-4 py-2">$0 - $11,600</td>
                  <td className="border border-slate-200 px-4 py-2">10%</td>
                  <td className="border border-slate-200 px-4 py-2">$1,160</td>
                </tr>
                <tr>
                  <td className="border border-slate-200 px-4 py-2">$11,601 - $47,150</td>
                  <td className="border border-slate-200 px-4 py-2">12%</td>
                  <td className="border border-slate-200 px-4 py-2">$2,445</td>
                </tr>
                <tr className="bg-slate-50 font-semibold">
                  <td className="border border-slate-200 px-4 py-2">Total Federal Income Tax</td>
                  <td className="border border-slate-200 px-4 py-2"></td>
                  <td className="border border-slate-200 px-4 py-2">$3,605</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">Total Federal Tax Liability:</h3>
            <ul className="space-y-2 text-slate-700">
              <li className="flex justify-between"><span>Federal Income Tax:</span><span className="font-semibold">$3,605</span></li>
              <li className="flex justify-between"><span>Self-Employment Tax:</span><span className="font-semibold">$8,852</span></li>
              <li className="flex justify-between border-t border-slate-300 pt-2 text-lg"><span><strong>Total Federal:</strong></span><span className="font-bold text-primary-700">$12,457</span></li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 5: Add State Income Tax</h2>
          <p className="text-slate-700 mb-4">
            Most states tax self-employment income at rates ranging from 2-13%:
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">No State Income Tax:</h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Alaska</li>
                <li>• Florida</li>
                <li>• Nevada</li>
                <li>• South Dakota</li>
                <li>• Tennessee</li>
                <li>• Texas</li>
                <li>• Washington</li>
                <li>• Wyoming</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-900 mb-2">Low Tax (2-5%):</h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Arizona: 2.59%</li>
                <li>• Colorado: 4.4%</li>
                <li>• Indiana: 3.15%</li>
                <li>• North Carolina: 4.75%</li>
                <li>• Utah: 4.85%</li>
              </ul>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">High Tax (&gt;9%):</h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• California: 9.3-13.3%</li>
                <li>• New York: 6.85-10.9%</li>
                <li>• New Jersey: 6.37-10.75%</li>
                <li>• Oregon: 8.75-9.9%</li>
                <li>• Minnesota: 7.05-9.85%</li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">Example (continued - California resident):</h3>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Taxable Income: $31,979</li>
              <li>• California tax rate: ~6% (effective)</li>
              <li>• State Tax: $31,979 × 6% = <strong>$1,919</strong></li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Step 6: Calculate Total Tax & Quarterly Payments</h2>
          
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-xl p-6 mb-6">
            <h3 className="text-2xl font-bold mb-4">Total Tax Summary:</h3>
            <ul className="space-y-2 text-lg">
              <li className="flex justify-between"><span>Federal Income Tax:</span><span>$3,605</span></li>
              <li className="flex justify-between"><span>Self-Employment Tax:</span><span>$8,852</span></li>
              <li className="flex justify-between"><span>State Income Tax (CA):</span><span>$1,919</span></li>
              <li className="flex justify-between border-t border-primary-400 pt-2 mt-2 text-2xl font-bold">
                <span>Total Tax Liability:</span>
                <span>$14,376</span>
              </li>
            </ul>
            <p className="mt-4 text-primary-100 text-sm">
              On $88,500 gross income = <strong>16.2% effective tax rate</strong> (after deductions)
            </p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-600 p-5 rounded mb-6">
            <h3 className="font-semibold text-slate-800 mb-2">Quarterly Payment Breakdown:</h3>
            <p className="text-slate-700 text-sm mb-3">
              Divide annual tax by 4 and pay by these deadlines to avoid penalties:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white rounded p-3 border border-amber-200">
                <p className="font-semibold text-amber-900">Q1 - Apr 15</p>
                <p className="text-lg font-bold text-slate-800">$3,594</p>
              </div>
              <div className="bg-white rounded p-3 border border-amber-200">
                <p className="font-semibold text-amber-900">Q2 - Jun 15</p>
                <p className="text-lg font-bold text-slate-800">$3,594</p>
              </div>
              <div className="bg-white rounded p-3 border border-amber-200">
                <p className="font-semibold text-amber-900">Q3 - Sep 15</p>
                <p className="text-lg font-bold text-slate-800">$3,594</p>
              </div>
              <div className="bg-white rounded p-3 border border-amber-200">
                <p className="font-semibold text-amber-900">Q4 - Jan 15</p>
                <p className="text-lg font-bold text-slate-800">$3,594</p>
              </div>
            </div>
          </div>

          <Link 
            to="/quarterly-tax-calculator"
            className="block bg-primary-600 text-white text-center px-6 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Calculate My Quarterly Payments →
          </Link>
        </section>

        {/* Expense Tracker CTA */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <svg className="w-12 h-12 flex-shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">Never Miss a Deduction Again</h3>
              <p className="text-green-100 mb-4 text-sm">
                Track every business expense automatically. Our free expense tracker categorizes receipts, calculates mileage, and maximizes your deductions.
              </p>
              <Link 
                to="/expense-tracker" 
                className="inline-block bg-white text-green-700 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors shadow-md"
              >
                Start Tracking Free →
              </Link>
            </div>
          </div>
        </div>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Quick Tax Estimation Rules of Thumb</h2>
          <div className="space-y-4">
            <div className="bg-blue-50 border-l-4 border-primary-600 p-5 rounded">
              <h3 className="font-semibold text-slate-800 mb-2">💰 Standard Set-Aside Rates:</h3>
              <ul className="text-slate-700 text-sm space-y-1">
                <li>• <strong>No state income tax states:</strong> Set aside 25-28% of gross income</li>
                <li>• <strong>Low-tax states (2-5%):</strong> Set aside 28-32% of gross income</li>
                <li>• <strong>High-tax states (&gt;9%):</strong> Set aside 32-38% of gross income</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-primary-600 p-5 rounded">
              <h3 className="font-semibold text-slate-800 mb-2">📊 By Income Level:</h3>
              <ul className="text-slate-700 text-sm space-y-1">
                <li>• <strong>Under $50k:</strong> ~20-25% (lower brackets + deductions)</li>
                <li>• <strong>$50k-$100k:</strong> ~25-30% (12-22% brackets + SE tax)</li>
                <li>• <strong>$100k-$200k:</strong> ~30-35% (22-24% brackets + SE tax)</li>
                <li>• <strong>Over $200k:</strong> ~35-40% (higher brackets + Additional Medicare)</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Common Mistakes That Cost Money</h2>
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold mb-2">❌ Not tracking all deductible expenses</h3>
              <p className="text-sm text-slate-700">
                Missing even $10,000 in deductions = overpaying by $3,000-4,000 in taxes. Track everything meticulously.
              </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold mb-2">❌ Forgetting the QBI deduction</h3>
              <p className="text-sm text-slate-700">
                The 20% Qualified Business Income deduction can save thousands. Most freelancers qualify but forget to claim it.
              </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold mb-2">❌ Not making quarterly payments</h3>
              <p className="text-sm text-slate-700">
                Waiting until April = underpayment penalties + interest. Pay quarterly or risk owing 5-10% extra in penalties.
              </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold mb-2">❌ Using gross income instead of net</h3>
              <p className="text-sm text-slate-700">
                Self-employment tax is calculated on NET profit (after expenses), not gross. Don't overpay by using the wrong number.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Helpful Tools & Calculators</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/" className="block border-2 border-primary-300 bg-primary-50 rounded-lg p-5 hover:shadow-lg transition-all">
              <h3 className="font-bold text-primary-900 mb-1">1099 Tax Calculator</h3>
              <p className="text-sm text-slate-700">
                Instant federal, state, and SE tax estimates with quarterly breakdown.
              </p>
            </Link>
            
            <Link to="/expense-tracker" className="block border-2 border-green-300 bg-green-50 rounded-lg p-5 hover:shadow-lg transition-all">
              <h3 className="font-bold text-green-900 mb-1">Expense Tracker</h3>
              <p className="text-sm text-slate-700">
                Organize receipts, track mileage, and maximize deductions automatically.
              </p>
            </Link>

            <Link to="/quarterly-tax-calculator" className="block border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-primary-700 mb-1">Quarterly Tax Calculator</h3>
              <p className="text-sm text-slate-600">
                Calculate exact quarterly payments and never miss a deadline.
              </p>
            </Link>

            <Link to="/blog/do-you-need-to-pay-quarterly-taxes" className="block border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-primary-700 mb-1">Quarterly Tax Guide</h3>
              <p className="text-sm text-slate-600">
                Learn when quarterly taxes apply and how to avoid penalties.
              </p>
            </Link>
          </div>
        </section>

        <div className="bg-amber-50 border-l-4 border-amber-600 p-5 rounded">
          <p className="text-sm text-slate-800 mb-0">
            <strong>Disclaimer:</strong> This guide provides general tax calculation information for educational purposes. 
            Tax situations vary by individual circumstances, deductions, credits, and filing status. Always consult a 
            qualified CPA or tax professional for personalized advice before filing.
          </p>
        </div>
      </article>
    </main>
  );
}
